import React, { useEffect, useRef, useState } from 'react';
import DayPicker from 'react-day-picker';
import type { DayPickerProps } from 'react-day-picker';

import type { InputFieldV3Props } from '../../inputV3/InputFieldV3/InputFieldV3';
import InputFieldV3 from '../../inputV3/InputFieldV3/InputFieldV3';
import { defaultFormat, defaultParse, getNextValidDate } from '../../util/dateUtils';
// helpers
import { useClickOutside } from '../../../../hooks/useClickOutside';
// assets & styles
import 'react-day-picker/lib/style.css';
import { PickerContainer, StyledDayPicker } from './DatePickerInputV2.styles';

export interface DatePickerInputV2Props {
  /** calendar icon within the field */
  calendarIcon?: boolean;
  /** Classname passed to container of input and calendar */
  className?: string;
  /** handler for input change */
  inputOnChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** disabled state */
  disabled?: boolean;
  /** function to transform JS Date to desired string (return string) */
  formatDate?: (d?: Date) => string;
  /** function to validate Date (return boolean) */
  parseDate?: (d?: string) => Date | undefined;
  /** props passed to InputFieldV3 component */
  inputFieldProps?: InputFieldV3Props;
  /** props passed through to 'DayPicker' library compenent (https://react-day-picker.js.org/api/DayPicker) */
  datePickerOptions?: DayPickerProps;
  /** value extracted from input; external value from rff or redux-form */
  value?: string;
  /** boolean for whether to call input.onChange or update state value */
  isFormField?: boolean;
  /** boolean explicitly telling the day picker not to show months before the current month, defaults to false */
  preventPastMonthsView?: boolean;
  requiredValidationCb?: (isWithinRef: boolean) => void;
}

/*
 * ```import { DatePickerInputV2 } from '@zola/zola-ui/src/components/Form/DatePickerInputV2';```
 *
 *  Wrapper for react-day-picker and InputFieldV2
 */

const DatePickerInputV2 = ({
  calendarIcon = true,
  className,
  disabled,
  formatDate = defaultFormat,
  parseDate = defaultParse,
  inputOnChange,
  value: externalValue,
  inputFieldProps,
  datePickerOptions,
  isFormField = false,
  preventPastMonthsView = false,
  requiredValidationCb,
  ...rest
}: DatePickerInputV2Props): React.ReactElement => {
  // hide / show functionality
  const ref = useRef(null);
  const [showCal, setShowCal] = useState(false);

  // handling date value
  const initialInputValue = externalValue || '';
  const initialDate = externalValue && parseDate ? parseDate(externalValue) : undefined;
  const [inputValue, setInputValue] = useState(initialInputValue); // string of formatted date
  const [selectedDay, setSelectedDay] = useState(initialDate); // Date object

  // for form fields, updates flow from externalValue --> inputValue
  useEffect(() => {
    if (isFormField && externalValue !== inputValue) {
      setInputValue(externalValue || '');
    }
  }, [externalValue, inputValue, isFormField, parseDate]);

  // on updates to inputValue --> update selectedDay
  useEffect(() => {
    let newVal;
    // if can be parsed properly
    if (inputValue && parseDate && parseDate(inputValue)) {
      newVal = parseDate(inputValue);
    }
    // update the selectedDay
    setSelectedDay(newVal);
  }, [inputValue, isFormField, parseDate]);

  const onClickOutside = () => {
    setShowCal(false);
    if (requiredValidationCb) requiredValidationCb(showCal);
  };
  useClickOutside({ ref, onClickOutside, isOpen: showCal });

  /**
   * Handle input changes. Day picking is handled by a different method (handleDayClick)
   * @param e React.ChangeEvent<HTMLInputElement>
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (isFormField) {
      if (inputFieldProps?.onChange) inputFieldProps?.onChange(e);
    } else {
      setInputValue(e.target.value);
    }
    if (inputOnChange) inputOnChange(e);
  };

  /**
   * assign formatted date to input and hide date picker
   * @param day Date
   * @param day Date
   * @param day Date
   */
  const handleDayClick = (day: Date, _m: unknown, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const formattedDay = formatDate(day);
    e.target.value = formattedDay;
    handleInputChange(e);
    setShowCal(false);
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (inputFieldProps?.onFocus) inputFieldProps.onFocus(e);
  };

  const onClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    setShowCal(true);
  };

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setShowCal(!showCal);
    } else if (!showCal) {
      setShowCal(true);
    }
  };

  // *** set up props ***
  const inputProps = Object.assign({}, inputFieldProps, {
    // prop overrides:
    addOn: calendarIcon && 'calendar',
    autoComplete: 'off',
    type: 'text',
    value: inputValue,
    disabled: disabled || inputFieldProps?.disabled,
    onFocus,
    onClick,
    onKeyPress,
    onChange: handleInputChange,
    isControlled: true,
    ...rest,
  });
  const pickerClass = showCal ? 'active' : '';
  const pickerProps = {
    className: pickerClass,
    month: preventPastMonthsView ? getNextValidDate(selectedDay) : selectedDay,
    selectedDays: preventPastMonthsView ? getNextValidDate(selectedDay) : selectedDay,
    onDayClick: handleDayClick,
    onDayMouseDown: (day: unknown, m: unknown, e: MouseEvent) => {
      /**
       * because react-final-form validation is called onMouseDown and onMouseUp,
       * prevent default so that 1. validation not run onMouseDown, 2. handleDayClick is called, 3. then validation run onMouseUp only
       */
      e.preventDefault();
    },
    ...datePickerOptions,
  };

  return (
    <div ref={ref} className={className}>
      <InputFieldV3 {...inputProps}>
        <PickerContainer>
          {showCal && (
            <StyledDayPicker>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore Daypicker returns a mouse click event TS will complain */}
              <DayPicker {...pickerProps} />
            </StyledDayPicker>
          )}
        </PickerContainer>
      </InputFieldV3>
    </div>
  );
};

export default DatePickerInputV2;
